import { useState, useEffect } from 'react';
import { apiFetch } from '../fetchInterceptor';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { Zap, Sparkles, Gem, ChevronDown, Check } from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODE_ICONS = {
  fast: Zap,
  balanced: Sparkles,
  premium: Gem,
};
const MODE_COLORS = {
  fast: 'text-amber-500',
  balanced: 'text-blue-500',
  premium: 'text-violet-500',
};

let cachedModes = null;

export default function AIModeSelector({ value = 'balanced', onChange, size = 'sm' }) {
  const [modes, setModes] = useState(cachedModes || []);

  useEffect(() => {
    if (cachedModes) return;
    apiFetch(`${API_URL}/api/ai-modes`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        cachedModes = data;
        setModes(data);
      })
      .catch(() => {});
  }, []);

  const current = modes.find(m => m.key === value) || { key: value, label: value };
  const Icon = MODE_ICONS[current.key] || Sparkles;
  const color = MODE_COLORS[current.key] || 'text-muted-foreground';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border bg-card hover:bg-muted/40 transition-colors",
            size === 'sm' ? "px-2 py-1 text-xs" : "px-3 py-1.5 text-sm"
          )}
          data-testid="ai-mode-selector"
        >
          <Icon className={cn(size === 'sm' ? "w-3 h-3" : "w-3.5 h-3.5", color)} />
          <span className="font-medium">{current.label}</span>
          <ChevronDown className="w-3 h-3 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="text-xs">AI Routing Mode</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {modes.map(m => {
          const ModeIcon = MODE_ICONS[m.key] || Sparkles;
          const modeColor = MODE_COLORS[m.key] || 'text-muted-foreground';
          const selected = m.key === value;
          return (
            <DropdownMenuItem
              key={m.key}
              onClick={() => onChange(m.key)}
              className="flex items-start gap-2 cursor-pointer py-2"
              data-testid={`ai-mode-${m.key}`}
            >
              <ModeIcon className={cn("w-4 h-4 flex-shrink-0 mt-0.5", modeColor)} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-medium text-sm">{m.label}</span>
                  {selected && <Badge variant="secondary" className="text-[9px] py-0">Active</Badge>}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{m.description}</p>
              </div>
              {selected && <Check className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-1" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
